import { Field } from '@amore/ui/components/field'
import { FormGrid, FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { TimePicker } from '@amore/ui/components/time-picker'
import { Text } from '@amore/ui/components/typography'
import { CalendarDays, Clock3, Heart, MapPin, MapPinned } from 'lucide-react'
import { useRef, useState } from 'react'
import { formatDate, newId } from '@/lib/format'
import { useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Ceremony } from '@/mock/types'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { durationLabel } from '../schedule/model'
import {
  type CeremonyBasics,
  type CeremonyBasicsErrors,
  ceremonyBasics,
  saveCeremonyBasics,
  validateCeremonyBasics,
} from './basics-model'
import { ceremonyKindLabels } from './model'

const kinds: NonNullable<Ceremony['kind']>[] = ['civil', 'religious', 'symbolic']
const softControl = 'h-10 border-transparent bg-muted/60 hover:bg-muted'
const undecided = 'undecided'

const timeLabels = {
  open: 'Vybrat čas',
  title: 'Vybrat čas',
  close: 'Zavřít výběr času',
  hours: 'Hodiny',
  minutes: 'Minuty',
  clear: 'Bez času',
  confirm: 'Potvrdit čas',
  format: '24hodinový formát · HH:mm',
}

export function CeremonyBasicsSheet({ onClose }: { onClose: () => void }) {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const [draft, setDraft] = useState(() => ceremonyBasics(data))
  const [errors, setErrors] = useState<CeremonyBasicsErrors>({})
  const formRef = useRef<HTMLFormElement>(null)
  const locationRef = useRef<HTMLInputElement>(null)
  const timeRef = useRef<HTMLInputElement>(null)
  const endTimeRef = useRef<HTMLInputElement>(null)
  const dirty = JSON.stringify(draft) !== JSON.stringify(ceremonyBasics(data))
  const venues = data.suppliers.filter(
    (supplier) =>
      supplier.category === 'venue' &&
      (supplier.status !== 'cancelled' || supplier.id === draft.venueSupplierId),
  )
  const selectedVenue = venues.find((venue) => venue.id === draft.venueSupplierId)
  const draftErrors = validateCeremonyBasics(draft)
  const duration =
    draftErrors.time || draftErrors.endTime
      ? null
      : durationLabel(draft.time || null, draft.endTime || null)
  const overnight = draft.time && draft.endTime && draft.endTime < draft.time

  const change = <K extends keyof CeremonyBasics>(field: K, value: CeremonyBasics[K]) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }
  const save = () => {
    if (!canEdit) return
    const nextErrors = validateCeremonyBasics(draft)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      const control = nextErrors.location
        ? locationRef.current
        : nextErrors.time
          ? timeRef.current
          : endTimeRef.current
      control?.focus()
      return
    }
    const eventId = newId('ceremony')
    update((current) => saveCeremonyBasics(current, draft, eventId))
    showToast({ title: 'Údaje o obřadu uloženy', tone: 'success' })
    onClose()
  }

  return (
    <DetailSheet
      open
      eyebrow="Obřad"
      title="Základní údaje obřadu"
      dirty={dirty}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      primaryAction={
        canEdit
          ? {
              label: 'Uložit',
              onClick: () => formRef.current?.requestSubmit(),
            }
          : undefined
      }
    >
      <FormLayout
        ref={formRef}
        className="divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1"
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          save()
        }}
      >
        <fieldset disabled={!canEdit} className="grid grid-cols-2 items-start gap-x-6 gap-y-7">
          <legend className="sr-only">Základní údaje obřadu</legend>
          <Field
            label={
              <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                <Heart className="size-4" aria-hidden="true" />
                Typ obřadu
              </span>
            }
            controlId="ceremony-kind"
          >
            <Select
              name="ceremonyKind"
              variant="soft"
              className={softControl}
              value={draft.kind ?? undecided}
              onValueChange={(value) =>
                change('kind', kinds.find((kind) => kind === value) ?? null)
              }
            >
              <SelectItem value={undecided}>Ještě nevíme</SelectItem>
              {kinds.map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {ceremonyKindLabels[kind]}
                </SelectItem>
              ))}
            </Select>
          </Field>
          <div className="space-y-2.5">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <CalendarDays className="size-4" aria-hidden="true" />
              Datum svatby
            </span>
            <Text>
              {data.identity.date ? formatDate(data.identity.date, 'long') : 'Zatím nevybrané'}
            </Text>
            <Text variant="small" tone="muted">
              Z údajů o svatbě.
            </Text>
          </div>
        </fieldset>
        <fieldset disabled={!canEdit} className="space-y-7">
          <legend className="sr-only">Místo obřadu</legend>
          <Field
            label={
              <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                <MapPin className="size-4" aria-hidden="true" />
                Místo obřadu
              </span>
            }
            controlId="ceremony-venue"
            description={
              venues.length
                ? 'Vybíráte z míst v Mých dodavatelích.'
                : 'Místo můžete vybrat, až ho přidáte do Mých dodavatelů.'
            }
          >
            <Select
              name="ceremonyVenue"
              variant="soft"
              className={softControl}
              value={draft.venueSupplierId ?? undecided}
              onValueChange={(value) =>
                change('venueSupplierId', venues.find((venue) => venue.id === value)?.id ?? null)
              }
            >
              <SelectItem value={undecided}>Zatím nevybrané</SelectItem>
              {draft.venueSupplierId && !selectedVenue ? (
                <SelectItem value={draft.venueSupplierId}>Dříve vybrané místo</SelectItem>
              ) : null}
              {venues.map((venue) => (
                <SelectItem key={venue.id} value={venue.id}>
                  {venue.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
          <Field
            label={
              <span className="flex items-center gap-1.5 text-sm font-normal text-muted-foreground">
                <MapPinned className="size-4" aria-hidden="true" />
                Konkrétní prostor
              </span>
            }
            controlId="ceremony-location"
            error={errors.location}
          >
            <Input
              ref={locationRef}
              name="ceremonyLocation"
              variant="soft"
              className={softControl}
              value={draft.location}
              maxLength={300}
              placeholder="Například zahrada, kaple nebo terasa"
              onChange={(event) => change('location', event.target.value)}
            />
          </Field>
        </fieldset>
        <fieldset disabled={!canEdit} className="space-y-2">
          <legend className="sr-only">Čas obřadu</legend>
          <FormGrid className="grid-cols-2 items-start gap-x-6">
            <Field
              label={
                <span className="flex min-h-10 items-center gap-1.5 text-sm font-normal text-muted-foreground sm:min-h-0">
                  <Clock3 className="size-4 shrink-0" aria-hidden="true" />
                  Začátek obřadu
                </span>
              }
              controlId="ceremony-start"
              error={errors.time}
            >
              <TimePicker
                ref={timeRef}
                name="ceremonyStart"
                variant="soft"
                className={softControl}
                value={draft.time}
                onValueChange={(value) => change('time', value)}
                labels={{ ...timeLabels, open: 'Vybrat začátek obřadu' }}
                placeholder="Zatím nevíme"
              />
            </Field>
            <Field
              label={
                <span className="flex min-h-10 items-center gap-1.5 text-sm font-normal text-muted-foreground sm:min-h-0">
                  <Clock3 className="size-4 shrink-0" aria-hidden="true" />
                  Předpokládaný konec
                </span>
              }
              controlId="ceremony-end"
              error={errors.endTime}
            >
              <TimePicker
                ref={endTimeRef}
                name="ceremonyEnd"
                variant="soft"
                className={softControl}
                value={draft.endTime}
                onValueChange={(value) => change('endTime', value)}
                labels={{ ...timeLabels, open: 'Vybrat konec obřadu' }}
                placeholder="Zatím nevíme"
              />
            </Field>
          </FormGrid>
          <Text variant="small" tone="muted">
            Změny času se projeví i v harmonogramu.
            {duration && !errors.time && !errors.endTime
              ? ` ${duration}${overnight ? ', konec je následující den' : ''}.`
              : ''}
          </Text>
        </fieldset>
      </FormLayout>
    </DetailSheet>
  )
}
